import * as React from "react";
import { Alert, AlertTitle, Link, Typography, Stack } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import DetailPage, { DetailPageTab } from "../../../components/common/detailPage/detailPage";
import { SimpleBreadcrumbs } from "../../../components/navigation/simpleBreadcrumbs";
import UserApplicationsPanel from "./applications/userApplicationsPanel";
import UserGeneralPanel from "./general/userGeneralPanel";
import UserNotificationsPanel from "./notifications/userNotificationsPanel";
import UserSessionsPanel from "./sessions/userSessionsPanel";
import StandardLayout from "../../../layouts/standard";
import PageLoader from "../../../components/common/pageLoader";
import UserAuth0LoginPanel from "./auth0Logins/userAuth0LoginPanel";
import useCombinationKeyPress from "../../../hooks/useCombinationKeyPress";
import IndividualHistoryPanel from "../../../components/shared/individualHistoryPanel/individualHistoryPanel";
import {
    UserClaimsService,
    eGroupIds,
} from "../../../services/currentUserService/currentUserService";
import UserParticipantPanel from "./participants/userParticipantsPanel";
import {
    useGetDemographicsByProfileKeyQuery,
    useGetPkeyByImKeyQuery,
} from "../../../services/cclTokenedEnterpriseCoachingApi";
import { Profile } from "../../../services/types/enterpriseCoachingApiTypes";
import { RouteAccess } from "../../../app/slices/allowedRoutesSlice";
import UserJournalDrawer from "./userJournalDrawer";
import { useGetCclUserAccountDetailQuery } from "../../../services/cclTokenedGrandCentralApi";
import { EnterpriseUserDetail } from "../../../services/types/userPreferenceTypes";

const UserDetail: React.FC = () => {
    const claimsService = new UserClaimsService();
    const isCclAdmin = claimsService.IsUserCclAdminOrSuperAdmin();
    const isNwaAdmin = claimsService.IsUserNwaAdmin();
    const isClientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const [searchParams] = useSearchParams();
    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
    const [email, setEmail] = React.useState<string>("");
    const [imkey, setImkey] = React.useState<number>(-1);
    const [tabIndex, setTabIndex] = React.useState<number>(isNwaAdmin || isClientAdmin ? 2 : 0);
    const [journalKey, setJournalKey] = React.useState<number>(Date.now());
    const detailEl = React.useRef<HTMLDivElement>(null);
    const onKeyDown = (event: KeyboardEvent) => {
        setDrawerOpen(true);
        event.preventDefault();
    };
    useCombinationKeyPress(["j"], onKeyDown, detailEl.current);
    const { data: enterpriseData } = useGetCclUserAccountDetailQuery(email, {
        skip: email === "",
    });
    const { data: pkey } = useGetPkeyByImKeyQuery(imkey.toString(), { skip: imkey === -1 });
    const { data } = useGetDemographicsByProfileKeyQuery(`${pkey?.pkey}`, {
        skip: !pkey || !pkey.pkey,
    });
    const emptyProfile = {} as Profile;
    const profile = data == null ? emptyProfile : data;
    const enterpriseUsers =
        enterpriseData != null ? enterpriseData : ([] as EnterpriseUserDetail[]);

    React.useEffect(() => {
        const email = searchParams.get("email");

        setEmail(email ? decodeURIComponent(email) : "");
        setImkey(+(searchParams.get("imkey") ?? "-1"));
        setTabIndex(+(searchParams.get("tabIndex") ?? 2));
    }, [searchParams]);

    let tablist: DetailPageTab[] = [
        {
            label: "Details",
            index: 2,
            content: (
                <UserApplicationsPanel
                    emailAddress={email}
                    firstName={enterpriseUsers.length > 0 ? enterpriseUsers[0].firstName : ""}
                    lastName={enterpriseUsers.length > 0 ? enterpriseUsers[0].lastName : ""}
                    imKey={imkey.toString()}
                    profile={profile}
                    clientAdmin={isClientAdmin}
                />
            ),
            visible: isCclAdmin || isNwaAdmin || isClientAdmin,
        },
        {
            label: "Accounts",
            index: 0,
            content: <UserGeneralPanel emailAddress={email} imkey={imkey} profile={profile} />,
            visible: isCclAdmin,
        },
        {
            label: "Logins",
            index: 3,
            content: <UserAuth0LoginPanel UserEmail={email} />,
            visible: isCclAdmin || isNwaAdmin || isClientAdmin,
        },
        {
            label: "Registrations",
            index: 4,
            content: <UserSessionsPanel emailAddress={email} />,
            visible: isCclAdmin,
        },
        {
            label: "Files",
            index: 7,
            content: (
                <UserParticipantPanel
                    imKey={imkey}
                    emailAddress={email}
                    fullName={`${enterpriseUsers.length > 0 ? enterpriseUsers[0].firstName : ""}${
                        enterpriseUsers.length > 0 ? enterpriseUsers[0].lastName : ""
                    }`}
                />
            ),
            visible: isCclAdmin,
        },
        {
            label: "Emails",
            index: 1,
            content: <UserNotificationsPanel emailAddress={email} />,
            visible: isCclAdmin || isNwaAdmin || isClientAdmin,
        },
        {
            label: "Logs",
            index: 6,
            content: (
                <IndividualHistoryPanel
                    journalTabKey={journalKey}
                    imKey={imkey.toString()}
                    emailAddress={email}
                    idPrefix="user-detail"
                    onCreateJournalEntry={() => setDrawerOpen(true)}
                />
            ),
            visible: isCclAdmin || isNwaAdmin || isClientAdmin,
        },
    ];

    const allowedRoutes: RouteAccess = claimsService.GetCurrentAllowedRoutes();

    const breadcrumbs = (
        <Stack direction={"row"} spacing={3}>
            <SimpleBreadcrumbs>
                {allowedRoutes.allowUsers ? (
                    <Typography variant="h6" color="text.primary">
                        <Link key="findUserLink" component={RouterLink} to="/users">
                            Users
                        </Link>
                    </Typography>
                ) : (
                    <Typography variant="h6" color="text.primary">
                        Users
                    </Typography>
                )}
                <Typography variant="h6" color="text.primary">
                    {email !== "" ? email : "Unknown"}
                </Typography>
            </SimpleBreadcrumbs>
        </Stack>
    );

    function onSaveJournalEntry() {
        setJournalKey(Date.now());
        setDrawerOpen(false);
    }

    function showErrorAlert(msg: string | undefined) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <div>
                    <Alert severity="error" sx={{ fontSize: 14 }}>
                        <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                        {msg}
                    </Alert>
                </div>
            </StandardLayout>
        );
    }

    if (email === "" && imkey === -1) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <PageLoader msg={""} />
            </StandardLayout>
        );
    }

    if (email !== "" || imkey !== -1) {
        return (
            <div ref={detailEl}>
                {isCclAdmin || isNwaAdmin || isClientAdmin ? (
                    <UserJournalDrawer
                        userId={imkey.toString()}
                        email={email}
                        open={drawerOpen}
                        onClose={() => setDrawerOpen(false)}
                        onSave={() => onSaveJournalEntry()}
                    />
                ) : null}
                <DetailPage
                    breadcrumbs={breadcrumbs}
                    descriptor={"users"}
                    tablist={tablist}
                    activeTab={tabIndex}
                ></DetailPage>
            </div>
        );
    }

    return showErrorAlert(`No user data found for this email ${email}`);
};

export default UserDetail;
